/** Conservative, source-labelled adapters. Never synthesize live observations. */
export const number = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
export const formatMetric = (value, suffix = '') => number(value) === null ? 'Unavailable' : `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })}${suffix}`;
export const perSession = (events, sessions) => number(events) !== null && number(sessions) !== null && sessions > 0 ? events / sessions : null;
export const reportAvailable = (ga4, report) => !ga4?.unavailable && ga4?.reports?.[report]?.unavailable === false;
export function propertyBase(property) {
  for (const value of [property?.site_url, property?.url, property?.id, property?.name]) {
    if (typeof value !== 'string') continue;
    try {
      const url = new URL(value.startsWith('sc-domain:') ? `https://${value.slice(10)}` : value.includes('://') ? value : `https://${value}`);
      if (url.hostname.includes('.') && !/\s/.test(value)) return url;
    } catch { /* Not a URL property. */ }
  }
  return null;
}
/** Preserve case, trailing slash, query order, percent escapes and host. Remove fragments only. */
export function pageIdentity(page, { hostname, base } = {}) {
  if (typeof page !== 'string' || !page || page === '(not set)' || page.startsWith('//')) return null;
  try {
    const absolute = /^https?:\/\//i.test(page);
    if (!absolute && !page.startsWith('/')) return null;
    const url = new URL(page, hostname ? `https://${hostname}` : base || undefined);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
    if (hostname && url.host.toLowerCase() !== hostname.toLowerCase()) return null;
    return { key: `${url.protocol}//${url.host}${url.pathname}${url.search}`, join: `${url.host}${url.pathname}${url.search}`, host: url.host, path: `${url.pathname}${url.search}`, url: `${url.protocol}//${url.host}${url.pathname}${url.search}` };
  } catch { return null; }
}
export function findingPages(finding) {
  const paths = [finding.page, finding.url, finding.path, finding.landing_page, ...(Array.isArray(finding.pages) ? finding.pages.map(row => typeof row === 'string' ? row : row?.page) : [])].filter(v => typeof v === 'string');
  // Accept explicitly labelled evidence only. Never infer a URL from a query or prose substring.
  if (typeof finding.evidence === 'string') {
    const match = finding.evidence.match(/^(?:Page|Landing page|URL|Path):\s*(\S+)\s*$/i);
    if (match) paths.push(match[1]);
  }
  return paths;
}
export function nativePageFindings(data) {
  if (data?.gsc?.unavailable) return [];
  return [
    ...(data?.ctr_benchmark_findings || []).map(finding => ({ ...finding, type: 'opportunity', source: 'Search Console', headline: finding.title, why_it_matters: finding.body,
      evidence: `Query: ${finding.query} · Page: ${finding.page} · ${formatMetric(finding.impressions)} impressions · ${formatMetric(finding.clicks)} clicks`,
    })),
    ...(data?.cannibalisation_findings || []).map(finding => ({ ...finding, type: 'warning', source: 'Search Console', headline: finding.title, why_it_matters: finding.body,
      evidence: `Query: ${finding.query} · ${finding.pages?.length || 0} returned pages · ${formatMetric(finding.combined_impressions)} combined impressions. Multiple pages appearing is a review signal, not proof of harm.`,
    })),
  ];
}
export function buildPageRows(data, findings = []) {
  const base = propertyBase(data?.property);
  const rows = new Map();
  const gscRows = data?.gsc?.unavailable ? [] : data?.gsc?.top_pages || [];
  for (const [i, row] of gscRows.entries()) {
    const identity = pageIdentity(row.page, { base });
    const key = identity?.key || `gsc:${i}:${row.page}`;
    rows.set(key, { key, identity, page: row.page, gsc: row, sources: ['Search Console'], findings: [] });
  }
  function addAnalytics(items, field, label) {
    for (const [i, row] of items.entries()) {
      // GA data spans the entire selected property. A missing host is ambiguous, not the selected website.
      const identity = row.hostname ? pageIdentity(row.page, { hostname: row.hostname }) : null;
      const matches = identity ? [...rows.values()].filter(r => r.identity?.join === identity.join) : [];
      const target = matches.length === 1 && matches[0].identity.key === identity.key && !matches[0][field] ? matches[0] : null;
      if (target) { target[field] = row; target.sources.push(label); }
      else {
        const key = `${field}:${i}:${identity?.key || row.page}`;
        rows.set(key, { key, identity, page: identity?.url || row.page, [field]: row, sources: [label], findings: [] });
      }
    }
  }
  if (reportAvailable(data?.ga4, 'top_pages')) addAnalytics(data.ga4.top_pages || [], 'analytics', 'GA4 page observations');
  if (!data?.ga4?.unavailable) addAnalytics((data?.ga4?.top_landing_pages || []).map(row => reportAvailable(data.ga4, 'landing_engagement') ? row : { ...row, hostname: null, engaged_sessions: null, engagement_rate: null }), 'landing', 'GA4 landing sessions');
  const candidates = [...nativePageFindings(data), ...findings];
  for (const row of rows.values()) {
    row.findings = candidates.filter(finding => finding.type !== "positive" && findingPages(finding).some(path => {
      const identity = pageIdentity(path, { base });
      return identity && row.identity && identity.key === row.identity.key;
    }));
    row.findings = row.findings.filter((finding, index, all) => all.findIndex(other => (other.headline || other.title || other.query) === (finding.headline || finding.title || finding.query)) === index);
    const observed = [row.gsc?.impressions, row.gsc?.clicks, row.analytics?.pageviews, row.landing?.sessions].some(v => number(v) !== null && v > 0);
    row.assessment = row.findings.length ? 'Worth reviewing' : observed ? 'No flagged opportunity in available data' : 'Not enough data';
  }
  return [...rows.values()];
}
export function sortRows(rows, key, ascending = false) {
  return [...rows].sort((a, b) => {
    const x = number(a[key]), y = number(b[key]);
    if (x === null) return y === null ? 0 : 1;
    if (y === null) return -1;
    return ascending ? x - y : y - x;
  });
}
export function reportingPeriod(data) {
  return data?.range_start && data?.range_end ? `${data.range_start} to ${data.range_end}` : 'Reporting dates unavailable';
}
